import { combineLatest, firstValueFrom, from, Observable, of, switchMap } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { Injectable } from '@angular/core'
import { locales, Localization } from '@aa-app-localization'

import {
    Dimensions,
    DimensionUnits,
    Product,
} from '@app-domains/content-blocks/components/product-card/product-card.types'
import { ProductHit, Color } from '@app-types/product-hit.types'
import { transformHit } from '@app-lib/product.lib'
import { productTypeFilter } from '@app-domains/meilisearch/lib/product-filter.lib'
import { mapSearchHitToProductHit } from '@app-domains/meilisearch/mappers/product-hit.mapper'
import { ProductSearchService } from '@app-domains/meilisearch/services/product-search.service'
import { ProductSearchHit } from '@app-domains/meilisearch/types/product-search.types'

import {
    ColorFragment,
    HighlightedProductsQueryService,
    ProductBySlugQuery,
    ProductBySlugQueryVariables,
    ProductFragment,
    ProductImageTypeEnum,
    ProductsByEansQuery,
    ProductsByEansQueryService,
    ProductsByEansQueryVariables,
    ProductsOrderPropertiesQuery,
    ProductsOrderPropertiesQueryService,
    ProductsOrderPropertiesQueryVariables,
    SimpleProductFragment,
    SimpleProductsByEanQuery,
    SimpleProductsByEanQueryService,
    SimpleProductsByEanQueryVariables,
    SuggestedRetailPricesEnum,
} from '@app-graphql/schema'

import { LocalizationService } from '@app-domains/localization/service/localization.service'
import { ProductHistoryService } from '@app-domains/product/services/product-history/product-history.service'
import { AuthService, WishlistService } from '@app-services'
import Locale = Localization.Locale
import { Apollo } from 'apollo-angular'

export type ObservableInput<T> =
    | Observable<T>
    | AsyncIterable<T>
    | PromiseLike<T>
    | ArrayLike<T>
    | Iterable<T>

export type ObservableInputTuple<T> = {
    [K in keyof T]: ObservableInput<T[K]>
}

@Injectable({ providedIn: 'root' })
export class ProductService {

    currentProduct?: ProductFragment

    constructor(
        private productsByEanQueryService: ProductsByEansQueryService,
        private simpleProductsByEanQueryService: SimpleProductsByEanQueryService,
        private productsOrderPropertiesQueryService: ProductsOrderPropertiesQueryService,
        private highlightedProductsQueryService: HighlightedProductsQueryService,
        private productHistoryService: ProductHistoryService,
        private wishlistService: WishlistService,
        private authService: AuthService,
        private localization: LocalizationService,
        private productSearchService: ProductSearchService,
    ) {
    }

    public resolveProductForDetail(
        slug: string,
        locale: Locale,
    ): Observable<Product | 'not-found' | 'no-access'> {
        return this.getProductBySlug({ slug }, locale).pipe(
            switchMap((result) => {
                const productBySlug = result.data?.productBySlug

                if (productBySlug) {
                    return of(this.transformProductFragmentToProduct(productBySlug, locale)).pipe(
                        switchMap((product) => this.enhance([product])),
                        map(([product]) => product),
                    )
                }

                if (result.data?.productBySlug === null && ! result.error) {
                    return of<'no-access'>('no-access')
                }

                return from(this.fetchProductBySlugViaSearch(slug, locale)).pipe(
                    map((product) => product ?? 'not-found'),
                )
            }),
            catchError(() => of<'not-found'>('not-found')),
        )
    }

    public getProductBySlug(input: ProductBySlugQueryVariables, locale: Locale)
        : Observable<Apollo.QueryResult<ProductBySlugQuery>> {
        return this.productHistoryService.fetch(input, locale).pipe(
            tap((result) => this.currentProduct = result.data?.productBySlug ?? undefined),
            tap((result) => {
                if (result.data?.productBySlug) {
                    const { ean, translations } = result.data.productBySlug

                    for (const l of locales) {
                        void this.localization.patchTranslations(l, { ROUTES: { [`product-${ ean }`]: translations[l].slug } })
                    }
                }
            }),
        )
    }

    private async fetchProductBySlugViaSearch(slug: string, locale: Locale): Promise<Product | null> {
        const queries = [
            slug.replace(/-/g, ' '),
            slug,
        ]

        for (const query of queries) {
            const result = await this.productSearchService.execute({
                query,
                locale,
                page: 1,
                perPage: 24,
                semanticRatio: 0,
                filters: [productTypeFilter()],
                sort: [],
            })

            const hit = result.hits.find((candidate: ProductSearchHit) => candidate.slug === slug)

            if (! hit) {
                continue
            }

            const fullProduct = await this.fetchFullProductByEan(hit.ean, locale)
            const product = fullProduct ?? transformHit(mapSearchHitToProductHit(hit))
            const [enhanced] = await firstValueFrom(this.enhance([product]))

            this.localization.patchTranslations(locale, {
                ROUTES: {
                    [`product-${ hit.ean }`]: slug,
                },
            })

            return enhanced
        }

        return null
    }

    private async fetchFullProductByEan(ean: string, locale: Locale): Promise<Product | null> {
        try {
            const result = await firstValueFrom(
                this.getProducts({ eans: [ean] }).pipe(
                    catchError(() => of(null)),
                ),
            )

            if (! result) {
                return null
            }

            const fragment = result.data?.productsByEan?.[0]

            if (! fragment || result.error) {
                return null
            }

            return this.transformProductFragmentToProduct(fragment, locale)
        } catch {
            return null
        }
    }

    public getSimpleProducts(input: SimpleProductsByEanQueryVariables)
        : Observable<Apollo.QueryResult<SimpleProductsByEanQuery>> {
        return this.simpleProductsByEanQueryService.fetch({ variables: input })
    }

    public getProducts(input: ProductsByEansQueryVariables)
        : Observable<Apollo.QueryResult<ProductsByEansQuery>> {
        return this.productsByEanQueryService.fetch({ variables: input })
    }

    public getProductsOrderProperties(input: ProductsOrderPropertiesQueryVariables)
        : Observable<Apollo.QueryResult<ProductsOrderPropertiesQuery>> {
        return this.productsOrderPropertiesQueryService.fetch({ variables: input })
    }

    public getHighlightedProducts(): Observable<SimpleProductFragment[]> {
        return this.highlightedProductsQueryService.fetch().pipe(
            map((result) => result.data?.recommendedProducts?.data ?? []),
        )
    }

    public transformSimpleToProduct(simple: SimpleProductFragment, locale: Locale): Product
    public transformSimpleToProduct(simple: SimpleProductFragment[], locale: Locale): Product[]
    public transformSimpleToProduct(
        simple: SimpleProductFragment | SimpleProductFragment[],
        locale: Locale,
    ): Product | Product[] {

        if (Array.isArray(simple)) {
            return simple.map((s) => this.transformSimpleProduct(s, locale))
        }

        return this.transformSimpleProduct(simple, locale)
    }

    public transformHitToProduct(hits: ProductHit): Product
    public transformHitToProduct(hits: ProductHit[]): Product[]
    public transformHitToProduct(
        hits: ProductHit | ProductHit[],
    ): Product | Product[] {
        if (Array.isArray(hits)) {
            return hits.map(transformHit)
        }
        return transformHit(hits)
    }

    public transformProductFragmentToProduct(pf: ProductFragment, locale: Locale): Product
    public transformProductFragmentToProduct(pf: ProductFragment[], locale: Locale): Product[]
    public transformProductFragmentToProduct(
        pf: ProductFragment | ProductFragment[],
        locale: Locale,
    ): Product | Product[] {
        if (Array.isArray(pf)) {
            return pf.map((p) => ProductService.transformProduct(p, locale))
        }
        return ProductService.transformProduct(pf, locale)
    }

    public enhanceProducts<
        T extends readonly unknown[],
        P extends Product | ProductFragment,
    >(
        products: P[],
        deps: [...ObservableInputTuple<T>],
        zipFn: (products: P[], deps: T) => P[],
        enhanceFn: (product: P, deps: T) => P,
    ): Observable<P[]> {
        return combineLatest(deps).pipe(
            map((resolvedDeps) => zipFn(products, resolvedDeps as T).map(
                (product) => enhanceFn(product, resolvedDeps as T)),
            ),
        )
    }

    public enhance<P extends Product | ProductFragment>(products: P[]): Observable<P[]> {
        const productsOrderProperties$ = this.authService.user$.pipe(
            switchMap((user) => user && user.debtorCode
                ? this.getProductsOrderProperties({ eans: products.map(p => p.ean) })
                : of({ data: undefined }),
            ),
            map((result) => result.data?.productsByEan ?? []),
        )
        return this.enhanceProducts(
            products,
            [
                this.wishlistService.wishlist$,
                productsOrderProperties$,
            ],
            (prods, [, productsProperties]) => {
                return prods.map((p) => {
                    const productProperties = productsProperties.find((product) => product.ean === p.ean)!
                    return {
                        ...p,
                        newDebtorPrice: productProperties?.price?.price,
                        debtorPrice: productProperties?.price?.standardPrice,
                        inStock: productProperties?.stock?.quantity > 0,
                    }
                })
            },
            (p, resolved) => {
                const [wishlist] = resolved
                return {
                    ...p,
                    inFavourites: !! wishlist.find((w) => w.ean === p.ean),
                }
            },
        )
    }

    public static transformToColor = (color: ColorFragment, locale: Locale): Color => ({
        presentation: color.presentation,
        name: color.translations[locale],
    })

    private transformSimpleProduct(item: SimpleProductFragment, locale: Locale): Product {
        const primaryImage = item.images.find(image => image.type === ProductImageTypeEnum.Front)?.url ?? ''
        const ambianceImage = item.images.find(image => image.type === ProductImageTypeEnum.Group)?.url ?? ''

        const subMaterials = item.subMaterials
            ? item.subMaterials.map(material => material[locale])
            : []

        const subColors = item.subColors
            ? item.subColors.map(color => ProductService.transformToColor(color, locale))
            : []

        const dimensions: Dimensions = {
            units: DimensionUnits.cm,
            height: 0,
            width: 0,
            diameter: 0,
            length: 0,
            ...Object.fromEntries(item.dimensions.map((d) => [
                d.label,
                d.dimension,
            ])),
        }

        const suggestedRetailPricesForDebtor = item.suggestedRetailPrices
            .find(price => price.type === SuggestedRetailPricesEnum.Debtor)

        const suggestedRetailPrice = suggestedRetailPricesForDebtor?.price
            ?? item.suggestedRetailPrices.find(price => price.type === SuggestedRetailPricesEnum.Nl)!.price
        const suggestedStandardRetailPrice = suggestedRetailPricesForDebtor?.standardPrice
            ?? item.suggestedRetailPrices.find(price => price.type === SuggestedRetailPricesEnum.Nl)!.standardPrice
        const suggestedRetailPriceISE = item.suggestedRetailPrices
            .find(price => price.type === SuggestedRetailPricesEnum.Ise)?.price ?? 0
        const suggestedStandardRetailPriceISE = item.suggestedRetailPrices
            .find(price => price.type === SuggestedRetailPricesEnum.Ise)?.standardPrice ?? 0

        const deliveryTimeInWeeks = item.orderProperties
            ? item.orderProperties?.deliveryInfo.deliverableInWeeks
            : null

        return {
            id: item.ean,
            uniqueHash: item.uniqueHash,
            collectionName: item.range,
            name: item.translations[locale].name,
            primaryImage,
            ambianceImage,
            wideImage: '',
            status: item.statusTranslations[locale],
            statusEnum: item.status,
            newProduct: false,
            brand: item.brand?.name ?? '',
            materials: [],
            subMaterials,
            colors: [],
            subColors,
            dimensions,
            codeLocal: item.codeLocal,
            ean: item.ean,
            slug: item.translations[locale].slug,
            debtorPrice: 0,
            newDebtorPrice: 1,
            inStock: !! item.stock,
            deliveryTimeInWeeks,
            suggestedRetailPrice,
            suggestedStandardRetailPrice,
            suggestedRetailPriceISE,
            suggestedStandardRetailPriceISE,
            inFavourites: false,
            images: item.images,
            totalPackages: '0',
            packages: [],
            productOrderSteps: 0,
            fsc: item.fsc,
        }
    }

    public static transformProduct(item: ProductFragment, locale: Locale): Product {
        const materials = item.materials.map(material => material[locale])

        const subMaterials = item.subMaterials
            ? item.subMaterials.map(material => material[locale])
            : []

        const colors = item.colors.map(color => ProductService.transformToColor(color, locale))

        const subColors = item.subColors
            ? item.subColors.map(color => ProductService.transformToColor(color, locale))
            : []

        const dimensions: Dimensions = {
            units: DimensionUnits.cm,
            height: 0,
            diameter: 0,
            width: 0,
            length: 0,
            ...Object.fromEntries(item.dimensions.map(value => [
                value.label,
                value.dimension,
            ])),
        }

        const suggestedRetailPricesForDebtor = item.suggestedRetailPrices
            .find(price => price.type === SuggestedRetailPricesEnum.Debtor)

        const suggestedRetailPrice = suggestedRetailPricesForDebtor?.price
            ?? item.suggestedRetailPrices.find(price => price.type === SuggestedRetailPricesEnum.Nl)!.price
        const suggestedStandardRetailPrice = suggestedRetailPricesForDebtor?.standardPrice
            ?? item.suggestedRetailPrices.find(price => price.type === SuggestedRetailPricesEnum.Nl)!.standardPrice
        const suggestedRetailPriceISE = item.suggestedRetailPrices
            .find(price => price.type === SuggestedRetailPricesEnum.Ise)?.price ?? 0
        const suggestedStandardRetailPriceISE = item.suggestedRetailPrices
            .find(price => price.type === SuggestedRetailPricesEnum.Ise)?.standardPrice ?? 0

        const deliveryTimeInWeeks = item.orderProperties
            ? item.orderProperties?.deliveryInfo.deliverableInWeeks
            : null

        // TODO: Align Product and SimpleProduct
        return {
            id: item.ean,
            uniqueHash: item.uniqueHash,
            collectionName: item.range,
            name: item.translations[locale].name,
            primaryImage: item.images[0]?.url ?? '',
            ambianceImage: item.images[1]?.url ?? '',
            wideImage: item.images[2]?.url ?? '',
            status: item.statusTranslations[locale],
            newProduct: false,
            brand: item.brand?.name ?? '',
            materials,
            subMaterials,
            colors,
            subColors,
            downloads: item.downloads,
            dimensions,
            categories: item.categories,
            codeLocal: item.codeLocal,
            ean: item.ean,
            slug: item.translations[locale].slug,
            debtorPrice: 0,
            newDebtorPrice: 1,
            suggestedRetailPrice,
            suggestedStandardRetailPrice,
            suggestedRetailPriceISE,
            suggestedStandardRetailPriceISE,
            range: item.range,
            group: item.group,
            description: item.translations[locale].shortDescription,
            fullDescription: item.translations[locale].fullDescription,
            variants: item.variants || undefined,
            inStock: false,
            deliveryTimeInWeeks,
            images: item.images,
            videos: item.videos,
            country: item.country,
            inFavourites: true,
            totalPackages: item.totalPackages.toString(),
            packages: item.packages,
            productOrderSteps: item.orderProperties?.productOrderSteps,
            fsc: item.fsc,
            statusEnum: item.status,
            url3dModel: item.assetUrl3dModel || undefined,
        }
    }

    public async inWishlist(products: Product[]): Promise<void> {
        const wishlist = await firstValueFrom(this.wishlistService.wishlist$)

        if (! wishlist) {
            return
        }

        products.map((p) => p.inFavourites = !! wishlist.find((w) => w.ean === p.ean))
    }

}
